import {useEffect, useState} from "react";
import { connect } from "react-redux";
import StatsCharts from "./StatsCharts";
import { StatsTables, PayrollTables, AutomationTables } from "./StatsTables";
import { DashboardCard, PayrollStatusCard, FeatureUsageCard } from "./DashboardCards";
import SplineChart from "./SplineChart";
import ColumnChart from "./ColumnChart";
import PolarChart from "./PolarChart";
import {format} from "date-fns";
import { Actions as ADVISOR_ACTIONS } from "../../../store/actions/advisorActions";

const formatNo = (num) => num.toLocaleString(undefined, {
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

/**
 * Time savings for each automation in minutes
 * @type {{}}
 */
const timeSavings = {
	draftPayroll: 15,
	dataRequest: 5,
	clientReview: 15,
	sendPayslips: 5,
	pensions: 10,
	journals: 5,
	fps: 5,
	eps: 5
};

const AdvisorStats = ({
	  stats,
	  latestStats,
	  currency,
	  upcomingAutomations,
	  pastAutomations,
	  payrollTables,
	  splineChartData,
	starterLeaverData,
	getPayrollStats,
	getScheduledTasks,
	upcomingPayrolls,
	pastPayrolls,
	payrollOverdueStats,
	currentMonthPayrollStats
}) => {

	useEffect(() => {
		getPayrollStats();
		getScheduledTasks();
	},[]);

	if (!latestStats) return <></>;

	return (
		<>
			<div
				className="flex flex-wrap justify-evenly"
				style={{ gap: "10px" }}
			>
				<DashboardCard
					value={formatNo(latestStats.businesses.count)}
					subtitle={"Clients"}
				/>
				<DashboardCard
					value={formatNo(latestStats.people.count)}
					subtitle={"Employees"}
				/>
				<DashboardCard
					value={`${currency} ${formatNo(latestStats.payroll.gross)}`}
					subtitle={"Payroll processed this year"}
				/>
				<DashboardCard
					value={formatNo(latestStats.payroll.count)}
					subtitle={"Payrolls processed this month"}
				/>
			</div>

			<div className="flex flex-wrap justify-evenly">
				<PayrollStatusCard
					stats={payrollOverdueStats}
					countPerStatus={currentMonthPayrollStats.status}
				/>
			</div>

			<div
				className="grid grid-cols-5 wrap"
				style={{ gap: "20px", padding: "10px" }}
			>
				<div className="col-span-3">
					<SplineChart data={splineChartData}/>
				</div>
				<div className="flex items-center justify-center col-span-2">
					<PolarChart
						title={"EverRoll Usage"}
						data = {[
							{ name: "Draft Payroll", y: latestStats.everroll.draftPayroll ?? 0 / latestStats.payroll.count * 100},
							{ name: "Client Data Request", y: latestStats.everroll.dataRequest ?? 0 / latestStats.payroll.count * 100},
							{ name: "Client Review", y: latestStats.everroll.clientReview ?? 0 / latestStats.payroll.count * 100},
							{ name: "Send Payslips", y: latestStats.everroll.sendPayslips ?? 0 / latestStats.payroll.count * 100},
							{ name: "Pensions", y: latestStats.everroll.pensions ?? 0 / latestStats.payroll.count * 100},
							{ name: "Journal Entries", y: latestStats.everroll.journals ?? 0 / latestStats.payroll.count * 100},
							{ name: "FPS", y: latestStats.everroll.fps ?? 0 / latestStats.payroll.count * 100},
							{ name: "EPS", y: latestStats.everroll.eps ?? 0 / latestStats.payroll.count * 100},
						]}
					/>
				</div>
			</div>

			<div
				className="grid grid-cols-5 wrap-sm"
				style={{ gap: "20px", padding: "10px" }}
			>
				<div className="col-span-3">
					<ColumnChart data={starterLeaverData}/>
				</div>
				<div
					className="flex center flex-col col-span-2"
					style={{ gap: "20px" }}
				>
					<DashboardCard
						value={Object.keys(latestStats.everroll).reduce((acc, key) => acc + latestStats.everroll[key], 0)}
						subtitle={"Automations"}
					/>
					<DashboardCard
						value={Object.keys(latestStats.everroll).reduce((acc, key) => acc + latestStats.everroll[key] * (timeSavings[key] ?? 0), 0) / 60}
						subtitle={"Hours saved in the last month"}
					/>
				</div>
			</div>
			<PayrollTables upcoming={upcomingPayrolls}  past={pastPayrolls} />
			<AutomationTables upcoming={upcomingAutomations}  past={pastAutomations} />
			<div className="clearfix" />
		</>
	);
};

const mapStateToProps = (state) => {
	const jurisdiction = state?.advisors?.settings?.jurisdiction;
	const jurisdictionList = state.jurisdictions;

	const currency = jurisdictionList?.find((j) => j.countryCode === jurisdiction)?.currency;

	const settings = state.advisors.settings || {};

	const stats = settings?.stats

	const payrollTables =  [
		{ company: "Company A", date: "27th August" },
		{ company: "Company B", date: "29th August" },
	];

	const automations = state?.advisors?.scheduledTasks?.map(t => ({
		...t,
		date: t.date,
		company: t.companyName,
		name: t.action,
		status: t.status
	})) || [];

	const upcomingAutomations = automations.filter((automation) => new Date(automation.date) > new Date());

	const pastAutomations = automations.filter((automation) => new Date(automation.date) < new Date());

	const latestStats = stats && [...stats]?.pop();

	const splineChartData = stats?.map((stat) => {
		return {
			employees: stat.people.count,
			clients: stat.businesses.count,
			date: new Date(`${stat.period}-01`),
		};
	});

	const starterLeaverData = stats?.map((stat) => {
		return {
			starters: stat.people.joiners,
			leavers: stat.people.leavers,
			date: new Date(`${stat.period}-01`),
		};
	});

	const upcomingPayrolls = state?.advisors?.payroll?.upcomingPayrolls || [];
	const pastPayrolls = state?.advisors?.payroll?.pastPayrolls || [];

	const currentMonthPayrollStats = state?.advisors?.payroll?.stats?.[format(new Date(), "yyyy-MM")] || {};

	const payrollOverdueStats = {
		// total: currentMonthPayrollStats?.totalPayrolls,
		open: currentMonthPayrollStats?.totalPayrolls - currentMonthPayrollStats?.payrollsOverdue - currentMonthPayrollStats?.status?.Concluded,
		overdue: currentMonthPayrollStats?.payrollsOverdue,
		concluded: currentMonthPayrollStats?.status?.Concluded,
	};

	return {
		stats,
		payrollTables,
		latestStats,
		starterLeaverData,
		splineChartData,
		currency,
		upcomingAutomations,
		pastAutomations,
		upcomingPayrolls,
		pastPayrolls,
		payrollOverdueStats,
		currentMonthPayrollStats
	};
};

const mapDispatchToProps = (dispatch) => ({
	getPayrollStats: () => dispatch(ADVISOR_ACTIONS.getPayrollStats()),
	getScheduledTasks: () => dispatch(ADVISOR_ACTIONS.getScheduledTasks()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorStats);
